import { keys, uniqWith, isEqual } from "lodash";
import { useCallback, useState, useMemo } from "react";

export interface FieldError {
  code: string;
  message: string;
}

export interface ErrorsConfig {
  fieldNames: string[];
  nonFieldNames: string[];
}

export interface ErrorsState {
  fieldErrors: Record<string, FieldError[]>;
  nonFieldErrors: FieldError[];
}

export default function useErrors(config: ErrorsConfig) {
  const [errors, setErrors] = useState<ErrorsState>({
    fieldErrors: {},
    nonFieldErrors: [],
  });

  const hasErrors = useMemo(() => {
    const hasFieldErrors = Object.keys(errors.fieldErrors).length > 0;
    const hasNonFieldErrors = errors.nonFieldErrors.length > 0;

    return hasFieldErrors || hasNonFieldErrors;
  }, [errors.fieldErrors, errors.nonFieldErrors.length]);

  const mergeNonFieldErrors = useCallback(
    (errors: ErrorsState, newErrors: FieldError | FieldError[]) => {
      const newNonFieldErrors = Array.isArray(newErrors)
        ? errors.nonFieldErrors.concat(newErrors)
        : errors.nonFieldErrors.concat([newErrors]);

      return {
        ...errors,
        nonFieldErrors: uniqWith(newNonFieldErrors, isEqual),
      };
    },
    []
  );

  const mergeFieldErrors = useCallback(
    (errors: ErrorsState, fieldName: string, errorMessages: FieldError[]) => {
      return {
        ...errors,
        fieldErrors: {
          ...errors.fieldErrors,
          [fieldName]: errorMessages,
        },
      };
    },
    []
  );

  const resetErrors = useCallback(
    () =>
      setErrors({
        fieldErrors: {},
        nonFieldErrors: [],
      }),
    []
  );

  // TODO(Arthur): use correct error type
  const handleResponseErrors = useCallback(
    (response: any) => {
      let responseErrors: ErrorsState = {
        fieldErrors: {},
        nonFieldErrors: [],
      };
      if (response?.error?.data) {
        const { data } = response.error;

        if (config.fieldNames) {
          config.fieldNames.forEach((key) => {
            if (key in data) {
              responseErrors = mergeFieldErrors(responseErrors, key, data[key]);
            }
          });
        }

        if (config.nonFieldNames && data.non_field_errors) {
          const nonFieldNames = config.nonFieldNames ?? [];
          responseErrors = mergeNonFieldErrors(
            responseErrors,
            data.non_field_errors.filter((error: FieldError) =>
              nonFieldNames.includes(error.code)
            )
          );
        }
      }

      setErrors(responseErrors);
      return keys(responseErrors.fieldErrors).length ||
        responseErrors.nonFieldErrors.length
        ? responseErrors
        : null;
    },
    [
      config.fieldNames,
      config.nonFieldNames,
      mergeFieldErrors,
      mergeNonFieldErrors,
    ]
  );

  return { errors, hasErrors, resetErrors, handleResponseErrors };
}
